/* Featured collection plus: scroll-snap carousel arrows and auto-scroll (featured-collection-aligned.liquid).
   A custom element, so it re-initialises on its own when the theme editor re-renders the section. */
(function () {
  if (!window.customElements || customElements.get('fca-carousel')) return;

  class FcaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fca-track]');
      this.prev = this.querySelector('[data-fca-prev]');
      this.next = this.querySelector('[data-fca-next]');
      if (!this.track) return;

      this.update = this.update.bind(this);
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);

      // Track height changes as product images load, so this also re-centres the arrows
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.update);
        this.observer.observe(this.track);
      }
      this.update();
      this.initAutoplay();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      this.teardownAutoplay();
    }

    // One "page" = however many products are fully in view
    pageWidth() {
      const item = this.track.querySelector('.grid__item');
      if (!item || !item.offsetWidth) return this.track.clientWidth;
      const perView = Math.max(1, Math.round(this.track.clientWidth / item.offsetWidth));
      return item.offsetWidth * perView;
    }

    dir() {
      return getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    }

    go(direction) {
      this.track.scrollBy({ left: direction * this.dir() * this.pageWidth(), behavior: 'smooth' });
      this.restartTimer();
    }

    update() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const pos = Math.abs(track.scrollLeft);

      this.toggleAttribute('data-overflow', max > 1);
      if (this.prev) this.prev.disabled = pos <= 1;
      if (this.next) this.next.disabled = pos >= max - 1;

      // Centre the arrows on the product image rather than the whole card
      const image = track.querySelector('.grid-product__image-mask, .image-wrap');
      if (image && image.offsetHeight) {
        const top = image.getBoundingClientRect().top - this.getBoundingClientRect().top + image.offsetHeight / 2;
        this.style.setProperty('--fca-arrow-top', top + 'px');
      }
    }

    /* ---------- Auto-scroll ----------
       Moves one product per tick and loops to the start after the last one.
       Pauses while hovered, focused or touched, while off screen or in a background tab,
       and never runs for visitors who ask for reduced motion. */
    initAutoplay() {
      const seconds = parseFloat(this.dataset.autoplay);
      if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.delay = Math.max(2, seconds) * 1000;
      this.pauses = new Set();
      if (document.hidden) this.pauses.add('hidden');

      const pause = (reason) => () => { this.pauses.add(reason); this.restartTimer(); };
      const resume = (reason) => () => { this.pauses.delete(reason); this.restartTimer(); };

      this.autoplayListeners = [
        [this, 'mouseenter', pause('hover')],
        [this, 'mouseleave', resume('hover')],
        [this, 'focusin', pause('focus')],
        [this, 'focusout', (e) => { if (!this.contains(e.relatedTarget)) resume('focus')(); }],
        [this.track, 'touchstart', pause('touch')],
        [this.track, 'touchend', resume('touch')],
        [this.track, 'touchcancel', resume('touch')],
        [document, 'visibilitychange', () => { document.hidden ? pause('hidden')() : resume('hidden')(); }]
      ];
      this.autoplayListeners.forEach(([el, type, fn]) => el.addEventListener(type, fn, { passive: true }));

      if (window.IntersectionObserver) {
        this.pauses.add('offscreen');
        this.visibility = new IntersectionObserver((entries) => {
          entries[0].isIntersecting ? resume('offscreen')() : pause('offscreen')();
        });
        this.visibility.observe(this);
      }

      this.restartTimer();
    }

    teardownAutoplay() {
      clearInterval(this.timer);
      if (this.visibility) this.visibility.disconnect();
      if (this.autoplayListeners) {
        this.autoplayListeners.forEach(([el, type, fn]) => el.removeEventListener(type, fn));
      }
    }

    // Starts a fresh full interval, so the carousel never jumps straight after a shopper interacts
    restartTimer() {
      clearInterval(this.timer);
      if (!this.delay || this.pauses.size) return;
      this.timer = setInterval(() => this.autoAdvance(), this.delay);
    }

    autoAdvance() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 1) return;

      if (Math.abs(track.scrollLeft) >= max - 1) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        const item = track.querySelector('.grid__item');
        const step = item && item.offsetWidth ? item.offsetWidth : track.clientWidth;
        track.scrollBy({ left: this.dir() * step, behavior: 'smooth' });
      }
    }
  }

  customElements.define('fca-carousel', FcaCarousel);
})();